import { isDirectory } from './filesystem'

/**
 * Normalize a path by removing leading home (~/) and slash (/) prefixes
 * This is the most common path normalization pattern in the codebase.
 *
 * Examples:
 *   "~/projects" -> "projects"
 *   "/projects" -> "projects"
 *   "~" -> ""
 *   "~/" -> ""
 *   "" -> ""
 *   "projects/foo" -> "projects/foo"
 *
 * Note: Does NOT remove trailing slashes. Use normalizeDirectoryPath for that.
 */
export function normalizePath(path: string): string {
  return path
    .replace(/^~\/?/, '') // Remove leading ~/ or ~
    .replace(/^\//, '')   // Remove leading /
}

/**
 * Normalize a directory path to standard format
 * Examples:
 *   "projects/" -> "projects"
 *   "~/projects" -> "projects"
 *   "/projects" -> "projects"
 */
export function normalizeDirectoryPath(path: string): string {
  let normalized = path.trim()

  // Remove trailing slashes
  normalized = normalized.replace(/\/+$/, '')

  // Remove leading ~/ or /
  normalized = normalized.replace(/^~\//, '')
  normalized = normalized.replace(/^\//, '')

  // Handle just "~" or empty string as home
  if (normalized === '~' || normalized === '') {
    return '~'
  }

  return normalized
}

/**
 * Resolve parent directory from a path
 * Examples:
 *   "~/projects/YAPS" -> "~/projects"
 *   "~/projects" -> "~"
 *   "~" -> "~" (can't go above home)
 */
export function resolveParentDirectory(currentPath: string): string {
  const normalized = normalizeDirectoryPath(currentPath)

  // Already at home
  if (normalized === '~') {
    return '~'
  }

  // Split path and remove last component
  const parts = normalized.split('/')
  if (parts.length === 1) {
    return '~'
  }

  parts.pop()
  return parts.join('/')
}

/**
 * Resolve a target path relative to current directory
 * Handles:
 *   - Absolute paths: ~/projects/YAPS, /projects
 *   - Relative paths: ../skills, ../../thoughts
 *   - Current directory: . or ./something
 *   - Just names: YAPS, projects
 */
export function resolvePath(target: string, currentDirectory: string = '~'): string {
  const normalizedTarget = target.trim()

  // Empty or just "~" returns home
  if (!normalizedTarget || normalizedTarget === '~') {
    return '~'
  }

  // Absolute path (starts with ~/ or /)
  if (normalizedTarget.startsWith('~/') || normalizedTarget.startsWith('/')) {
    return normalizeDirectoryPath(normalizedTarget)
  }

  // Current directory reference
  if (normalizedTarget === '.') {
    return currentDirectory
  }

  // Handle relative paths with ../
  if (normalizedTarget.includes('../') || normalizedTarget.startsWith('..')) {
    let workingPath = currentDirectory
    const parts = normalizedTarget.split('/')

    for (const part of parts) {
      if (part === '..') {
        // Go up one level
        workingPath = resolveParentDirectory(workingPath)
      } else if (part === '.' || part === '') {
        // Stay at current level
        continue
      } else {
        // Navigate down
        if (workingPath === '~') {
          workingPath = part
        } else {
          workingPath = `${workingPath}/${part}`
        }
      }
    }

    return workingPath
  }

  // Handle ./ prefix (current directory relative)
  if (normalizedTarget.startsWith('./')) {
    const relativePart = normalizedTarget.substring(2)
    if (currentDirectory === '~') {
      return normalizeDirectoryPath(relativePart)
    }
    return normalizeDirectoryPath(`${currentDirectory}/${relativePart}`)
  }

  // Relative path without ../ (just a name or nested path)
  if (currentDirectory === '~') {
    return normalizeDirectoryPath(normalizedTarget)
  }

  // Append to current directory
  return normalizeDirectoryPath(`${currentDirectory}/${normalizedTarget}`)
}

/**
 * Convert a normalized path to display format
 * Examples:
 *   "projects" -> "~/projects"
 *   "~" -> "~"
 */
export function toDisplayPath(normalizedPath: string): string {
  if (normalizedPath === '~') {
    return '~'
  }
  return `~/${normalizedPath}`
}

/**
 * Check if a path is a valid directory in the virtual filesystem
 */
export function isValidDirectory(path: string): boolean {
  // Normalize the path first
  const normalized = normalizeDirectoryPath(path)

  // Home is always valid
  if (normalized === '~') {
    return true
  }

  // Check using filesystem utility
  return isDirectory(normalized)
}

/**
 * Extract the directory name from a path
 * Examples:
 *   "~/projects/YAPS" -> "YAPS"
 *   "projects" -> "projects"
 *   "~" -> "~"
 */
export function getDirectoryName(path: string): string {
  const normalized = normalizeDirectoryPath(path)

  if (normalized === '~') {
    return '~'
  }

  const parts = normalized.split('/')
  return parts[parts.length - 1]
}
